// src/hooks/useOrders.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ordersList,
  orderUpdate,
  ordersBatchUpdate,
  ordersStats,
} from "../../../../api/auth";

const DEFAULT_PAGE_SIZE = 20;

function cleanParams(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
}

export function useOrders(initial = {}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(0);

  const [page, setPage] = useState(initial.page || 1);
  const [pageSize, setPageSize] = useState(
    initial.page_size || DEFAULT_PAGE_SIZE
  );

  const [filters, setFilters] = useState({
    q: "",
    city: "",
    local_status: "",
    shipping_status: "",
    date_from: "",
    date_to: "",
    status: "",
    ...initial.filters,
  });

  // For optimistic updates: keep a map of dirty edits by orderId
  const [drafts, setDrafts] = useState({});
  const mountedRef = useRef(false);

  const params = useMemo(() => {
    return cleanParams({
      page,
      page_size: pageSize,
      ...filters,
    });
  }, [page, pageSize, filters]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ordersList(params);
      const data = res.data;
      const results = data.results ?? data ?? [];
      setOrders(
        
        
            results

      );
      setCount(data.count ?? results.length);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  const updateDraft = useCallback((orderId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [orderId]: { ...(prev[orderId] || {}), ...patch },
    }));

    // optimistic UI: update list view immediately
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o))
    );
  }, []);

  const clearDraft = useCallback((orderId) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  }, []);

  const saveOrder = useCallback(
    async (orderId) => {
      const patch = drafts[orderId];
      if (!patch) return;

      setSaving(true);
      setError(null);
      try {
        await orderUpdate(orderId, patch);
        clearDraft(orderId);
        await refresh(); // always refresh after save
      } catch (e) {
        setError(e);
        await refresh(); // revert optimistic if failed
      } finally {
        setSaving(false);
      }
    },
    [drafts, clearDraft, refresh]
  );

  const quickSetStatus = useCallback(
    async (orderId, local_status) => {
      updateDraft(orderId, { local_status });
      await saveOrder(orderId);
    },
    [saveOrder, updateDraft]
  );

  const bulkUpdate = useCallback(
    async ({ orderIds, patch }) => {
      if (!orderIds?.length) return;
      setSaving(true);
      setError(null);
      try {
        // optimistic
        setOrders((prev) =>
          prev.map((o) => (orderIds.includes(o.id) ? { ...o, ...patch } : o))
        );

        await ordersBatchUpdate({ order_ids: orderIds, patch });
        await refresh();
      } catch (e) {
        setError(e);
        await refresh();
      } finally {
        setSaving(false);
      }
    },
    [refresh]
  );

  const loadStats = useCallback(async (params = {}) => {
    const res = await ordersStats(cleanParams(params));
    return res.data;
  }, []);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((count || 0) / pageSize));
  }, [count, pageSize]);

  return {
    orders,
    count,
    page,
    pageSize,
    totalPages,
    loading,
    saving,
    error,

    filters,
    setFilters,
    setPage,
    setPageSize,

    drafts,
    updateDraft,
    saveOrder,
    quickSetStatus,
    bulkUpdate,

    refresh,
    loadStats,
  };
}